import { adsTimeForErgo, type WeaponStats } from "../combat/weapons.js";
import { ALL_ITEM_DEFS, AMMO_ITEMS, ATTACHMENT_ITEMS, getItemDef, WEAPON_ITEMS } from "./db.js";
import { ATTACH_KINDS, MAX_MOD_DEPTH } from "./types.js";
import type {
  AmmoItemDef,
  AttachKind,
  AttachmentItemDef,
  ItemDef,
  ItemInstance,
  MagazineItemDef,
  TacticalKind,
  WeaponItemDef,
  WeaponSlotDef,
} from "./types.js";

/**
 * Gun + bolted-on parts = the numbers the simulation uses. The second of two
 * folds: `weaponWithAmmo(aggregateWeaponStats(def, item), ammoDef)` —
 * attachments (handling, magazine, zero) first, then the cartridge (terminals).
 * Pure and rng-free; both sides run it, so the arithmetic is fixed:
 * multipliers compound in DEEP declaration order (`fittedModsDeep`; float
 * multiplication is not associative), `ergoAdd` sums and `adsTimeSec` is
 * recomputed from the clamped total, `zeroRangeSetM` replaces (last wins).
 *
 * Parts host parts, capped at `MAX_MOD_DEPTH`; every fold runs over the deep
 * list. The MAGAZINE dimension stays flat (a part may not declare a `mag`
 * slot): `magSize` is the fitted magazine's `magCapacity`, zero when nothing
 * is fitted.
 *
 * `mods` is untrusted input. A fitted entry counts only if it would have been
 * legal to fit (the same test `attach` applies); anything else is IGNORED, so
 * the gun still shoots — the only failure mode that cannot take a raid down.
 */

/** Slot + fitted attachment. `slot` is the slot on whatever host the part is
 *  actually screwed to, which for a nested part is another part. */
export interface FittedMod {
  slot: WeaponSlotDef;
  item: ItemInstance;
  def: AttachmentItemDef;
}

/**
 * `FittedMod` for a true magazine, def narrowed, so `def.magCapacity` and
 * `item.mag` need no re-proving.
 */
export interface FittedMagazine extends FittedMod {
  def: MagazineItemDef;
}

/**
 * The attachments fitted DIRECTLY to one host (a gun or a part), in `slots`
 * order, skipping empty slots and entries that fail the untrusted-input rules.
 * `item === null` is an empty list. ONE LEVEL ONLY — stat folds walk
 * `fittedModsDeep`; this stays flat so `fittedMagazine` can ask the flat question.
 */
export function fittedMods(
  def: WeaponItemDef | AttachmentItemDef,
  item: ItemInstance | null,
): FittedMod[] {
  const out: FittedMod[] = [];
  const slots = def.slots;
  if (slots === undefined || item === null) return out;
  const mods = item.mods;
  if (mods === undefined) return out;

  for (const slot of slots) {
    const fitted = mods[slot.id];
    if (fitted === undefined) continue;
    const attDef = getItemDef(fitted.defId);
    if (attDef === null || attDef.kind !== "attachment") continue;
    // `attach` already refuses these; failing here means a hand-built or
    // version-skewed instance. Ignore it rather than let a brake act as a
    // scope.
    if (attDef.attachKind !== slot.id) continue;
    if (!slot.accepts.includes(attDef.id)) continue;
    out.push({ slot, item: fitted, def: attDef });
  }
  return out;
}

/**
 * Every part on this gun in the order stats fold in: depth-first, each slot's
 * part then that part's own slots. The order is the aggregation contract.
 * Capped at `MAX_MOD_DEPTH` whatever the data says — honest content and
 * instances cannot reach it, so a payload that does is truncated.
 */
export function fittedModsDeep(def: WeaponItemDef, item: ItemInstance | null): FittedMod[] {
  const out: FittedMod[] = [];
  // `depth` is the depth of the parts this call is about to push: 1 for the
  // gun's own slots, 2 for theirs, and no call is made past the cap.
  const walk = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance | null,
    depth: number,
  ): void => {
    if (depth > MAX_MOD_DEPTH) return;
    for (const mod of fittedMods(hostDef, hostItem)) {
      out.push(mod);
      walk(mod.def, mod.item, depth + 1);
    }
  };
  walk(def, item, 1);
  return out;
}

/**
 * One node of a gun's part tree: the instance, its def, and its depth (0 = the
 * gun).
 */
export interface ModHost {
  item: ItemInstance;
  def: WeaponItemDef | AttachmentItemDef;
  /** 0 for the gun itself, 1 for a part on it, 2 for a part on that part. */
  depth: number;
}

/**
 * Resolves `hostIid` to the gun or to a legally-fitted part at any depth — the
 * door `attach`/`detach` go through. `null` when the id names nothing on this
 * gun, including a part `fittedMods` refuses to believe.
 */
export function findModHost(
  def: WeaponItemDef,
  item: ItemInstance,
  hostIid: string,
): ModHost | null {
  if (item.iid === hostIid) return { item, def, depth: 0 };
  const walk = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance,
    depth: number,
  ): ModHost | null => {
    if (depth >= MAX_MOD_DEPTH) return null;
    for (const mod of fittedMods(hostDef, hostItem)) {
      if (mod.item.iid === hostIid) return { item: mod.item, def: mod.def, depth: depth + 1 };
      const deeper = walk(mod.def, mod.item, depth + 1);
      if (deeper !== null) return deeper;
    }
    return null;
  };
  return walk(def, item, 0);
}

/**
 * Whether this def is a TRUE MAGAZINE — the presence of `magCapacity`, with
 * kind and `attachKind` checked too since ids arrive off the wire. The one
 * predicate everything routes through. Takes `ItemDef | null`.
 */
export function isTrueMagazine(def: ItemDef | null): def is MagazineItemDef {
  return (
    def !== null &&
    def.kind === "attachment" &&
    def.attachKind === "mag" &&
    def.magCapacity !== undefined
  );
}

/**
 * Whether this gun's rounds live in a detachable magazine — read off the
 * content: true exactly when its `mag` slot accepts a true magazine. True for
 * every gun in the roster (the shotgun's tube included); a gun with no `mag`
 * slot answers false and keeps its authored `stats.magSize`.
 */
export function weaponFeedsFromMag(def: WeaponItemDef): boolean {
  for (const slot of def.slots ?? []) {
    if (slot.id !== "mag") continue;
    for (const id of slot.accepts) {
      if (isTrueMagazine(getItemDef(id))) return true;
    }
    // One slot per kind, so there is no second `mag` slot to look at.
    return false;
  }
  return false;
}

/**
 * The true magazine fitted to this gun, or `null`. Runs on `fittedMods`, so a
 * forged or version-skewed entry answers `null` and the gun holds nothing.
 * `loadedRounds`/`loadedAmmo` are the doors through this. Walks the gun's own
 * slots only — a part may not declare a `mag` slot.
 */
export function fittedMagazine(
  def: WeaponItemDef,
  item: ItemInstance | null,
): FittedMagazine | null {
  for (const mod of fittedMods(def, item)) {
    if (mod.slot.id !== "mag") continue;
    const magDef = mod.def;
    if (isTrueMagazine(magDef)) return { slot: mod.slot, item: mod.item, def: magDef };
  }
  return null;
}

/**
 * The magazine a gun ships with, or `null` without a well. CONVENTION: the
 * factory magazine is listed FIRST in `accepts`; every other entry is an
 * upgrade. The one slot kept on this convention — every other factory part is
 * `factoryDefId`, which `validateItemDb` refuses on a `mag` slot.
 */
export function factoryMagFor(def: WeaponItemDef): MagazineItemDef | null {
  for (const slot of def.slots ?? []) {
    if (slot.id !== "mag") continue;
    for (const id of slot.accepts) {
      const accepted = getItemDef(id);
      if (isTrueMagazine(accepted)) return accepted;
    }
    return null;
  }
  return null;
}

/**
 * Rounds this gun holds: the fitted magazine's, or 0. THE accessor every
 * consumer goes through. Zero has three causes (no magazine, empty, not a
 * magazine) and one answer — why is the UI's question.
 */
export function loadedRounds(def: WeaponItemDef, item: ItemInstance | null): number {
  return fittedMagazine(def, item)?.item.mag ?? 0;
}

/**
 * Which cartridge is in the gun, or `null` when empty or the recorded id does
 * not resolve to a cartridge — never invented numbers.
 */
export function loadedAmmo(def: WeaponItemDef, item: ItemInstance | null): AmmoItemDef | null {
  const mag = fittedMagazine(def, item);
  if (mag === null || (mag.item.mag ?? 0) <= 0) return null;
  const id = mag.item.ammoDefId;
  if (id === undefined) return null;
  const ammo = getItemDef(id);
  return ammo !== null && ammo.kind === "ammo" ? ammo : null;
}

/**
 * A weapon instance with one slot set (or cleared), immutably. The map is
 * rebuilt in `ATTACH_KINDS` order so JSON key order never depends on fitting
 * order; the `mods` key is dropped when the last part comes off. Lives here
 * because `weaponWithMagRounds` and `planReload` need it without an inventory.
 */
export function weaponWithMod(
  item: ItemInstance,
  slot: AttachKind,
  fitted: ItemInstance | null,
): ItemInstance {
  const next: { [slotId: string]: ItemInstance } = {};
  const current = item.mods;
  for (const kind of ATTACH_KINDS) {
    const value = kind === slot ? fitted : current?.[kind] ?? null;
    if (value !== null) next[kind] = value;
  }
  const out: ItemInstance = { ...item };
  if (Object.keys(next).length === 0) delete out.mods;
  else out.mods = next;
  return out;
}

/**
 * `weaponWithMod` where the slot may belong to a PART of the gun. An immutable
 * path rebuild: only the host and its ancestors are replaced, siblings carried
 * by identity, each level through `weaponWithMod` so key order and the
 * empty-means-absent invariant hold at every depth. `null` when `hostIid`
 * names nothing on this gun.
 */
export function weaponWithModAt(
  def: WeaponItemDef,
  weapon: ItemInstance,
  hostIid: string,
  slot: AttachKind,
  fitted: ItemInstance | null,
): ItemInstance | null {
  if (weapon.iid === hostIid) return weaponWithMod(weapon, slot, fitted);

  const rebuild = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance,
    depth: number,
  ): ItemInstance | null => {
    if (depth >= MAX_MOD_DEPTH) return null;
    for (const mod of fittedMods(hostDef, hostItem)) {
      const replaced =
        mod.item.iid === hostIid
          ? weaponWithMod(mod.item, slot, fitted)
          : rebuild(mod.def, mod.item, depth + 1);
      if (replaced === null) continue;
      return weaponWithMod(hostItem, mod.slot.id, replaced);
    }
    return null;
  };
  return rebuild(def, weapon, 0);
}

/**
 * Whether this gun has all of itself: every `vital` slot, on the gun and on
 * every legally-fitted part recursively, holds a part the bench would accept.
 * `acceptFire` refuses "incomplete" when false. A missing part does not
 * expose its own vital slots (one missing part is one problem). Every gun
 * declares a vital barrel, so `item === null` is false across the roster.
 */
export function weaponComplete(def: WeaponItemDef, item: ItemInstance | null): boolean {
  const check = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance | null,
    depth: number,
  ): boolean => {
    const fitted = new Map<AttachKind, FittedMod>();
    for (const mod of fittedMods(hostDef, hostItem)) fitted.set(mod.slot.id, mod);
    for (const slot of hostDef.slots ?? []) {
      const got = fitted.get(slot.id);
      if (got === undefined) {
        if (slot.vital === true) return false;
        continue;
      }
      // Content cannot express a host at the cap, but a corrupt payload must
      // terminate.
      if (depth + 1 > MAX_MOD_DEPTH) continue;
      if (!check(got.def, got.item, depth + 1)) return false;
    }
    return true;
  };
  return check(def, item, 0);
}

/**
 * The parts a host ships with, minted fresh, recursively — one function for
 * the counter, the gun cabinet, a quest reward and a scav's shoulder. Two
 * conventions (see `WeaponSlotDef`): `mag` slots take the first true magazine
 * in `accepts`, empty; everything else takes `slot.factoryDefId`, absent
 * meaning genuinely empty. Rng-free; `nextIid` is called once per part in
 * `ATTACH_KINDS` order at each level, depth-first — callers that count ids
 * must ask, never assume. `undefined` for a bare host.
 */
export function mintFactoryMods(
  def: WeaponItemDef | AttachmentItemDef,
  nextIid: () => string,
  depth = 1,
): { [slotId: string]: ItemInstance } | undefined {
  if (depth > MAX_MOD_DEPTH) return undefined;

  // Decided in declaration order, minted in `ATTACH_KINDS` order — the
  // canonical key order `weaponWithMod` produces.
  const chosen = new Map<AttachKind, AttachmentItemDef>();
  for (const slot of def.slots ?? []) {
    if (slot.id === "mag") {
      for (const id of slot.accepts) {
        const accepted = getItemDef(id);
        if (isTrueMagazine(accepted)) {
          chosen.set("mag", accepted);
          break;
        }
      }
      continue;
    }
    const factoryId = slot.factoryDefId;
    if (factoryId === undefined) continue;
    const partDef = getItemDef(factoryId);
    // Unreachable for authored content; a bad edit ships without the part
    // rather than throwing inside a purchase.
    if (partDef === null || partDef.kind !== "attachment") continue;
    chosen.set(slot.id, partDef);
  }
  if (chosen.size === 0) return undefined;

  const out: { [slotId: string]: ItemInstance } = {};
  for (const kind of ATTACH_KINDS) {
    const partDef = chosen.get(kind);
    if (partDef === undefined) continue;
    const part: ItemInstance = { iid: nextIid(), defId: partDef.id, stack: 1 };
    const sub = mintFactoryMods(partDef, nextIid, depth + 1);
    if (sub !== undefined) part.mods = sub;
    out[kind] = part;
  }
  return out;
}

/**
 * The same weapon with the rounds in its fitted magazine replaced — the write
 * half of `loadedRounds` and the only way a count is set. Unchanged when no
 * magazine is fitted. `rounds` 0 drops both keys off the magazine; `ammoDefId`
 * `null` keeps what the magazine recorded (what a decrement wants).
 */
export function weaponWithMagRounds(
  weapon: ItemInstance,
  rounds: number,
  ammoDefId: string | null,
): ItemInstance {
  const def = getItemDef(weapon.defId);
  if (def === null || def.kind !== "weapon") return weapon;
  const fitted = fittedMagazine(def, weapon);
  if (fitted === null) return weapon;

  const next: ItemInstance = { ...fitted.item };
  if (rounds > 0) {
    next.mag = rounds;
    const id = ammoDefId ?? fitted.item.ammoDefId;
    if (id !== undefined) next.ammoDefId = id;
    else delete next.ammoDefId;
  } else {
    delete next.mag;
    delete next.ammoDefId;
    // An empty box has nothing left to have counted (protocol v27): the third
    // key goes with the other two, so "emptied" stays byte-identical to "fresh".
    delete next.magKnown;
  }
  return weaponWithMod(weapon, "mag", next);
}

// ---------------------------------------------------------------------------
// Counted magazines (protocol v27)
// ---------------------------------------------------------------------------

/**
 * Whether the player knows exactly how many rounds are in this magazine: the
 * box is full, or empty, or has been looked at since last fired
 * (`ItemInstance.magKnown`). Nothing should read `magKnown` raw. Takes the
 * magazine's def; `loadedRoundsKnown` is the gun-shaped wrapper.
 */
export function magCountKnown(def: MagazineItemDef, item: ItemInstance): boolean {
  const rounds = item.mag ?? 0;
  return rounds <= 0 || rounds >= def.magCapacity || item.magKnown === true;
}

/**
 * `magCountKnown` for the magazine in the gun. No magazine answers `true` — an
 * empty well's zero is exact.
 */
export function loadedRoundsKnown(def: WeaponItemDef, item: ItemInstance | null): boolean {
  const fitted = fittedMagazine(def, item);
  return fitted === null || magCountKnown(fitted.def, fitted.item);
}

/**
 * The magazine with its count marked known or forgotten, immutably.
 * Idempotent and minimal: marking an already-known box (full, empty, counted)
 * returns the SAME instance; `known` false deletes the key.
 */
export function withMagKnown(def: MagazineItemDef, item: ItemInstance, known: boolean): ItemInstance {
  if (known) {
    if (magCountKnown(def, item)) return item;
    return { ...item, magKnown: true };
  }
  if (item.magKnown !== true) return item;
  const next: ItemInstance = { ...item };
  delete next.magKnown;
  return next;
}

/**
 * A weapon whose fitted magazine has been counted or fired from. Unchanged when
 * no magazine, not a weapon, or the key is already right (called per shot).
 */
export function weaponWithMagKnown(weapon: ItemInstance, known: boolean): ItemInstance {
  const def = getItemDef(weapon.defId);
  if (def === null || def.kind !== "weapon") return weapon;
  const fitted = fittedMagazine(def, weapon);
  if (fitted === null) return weapon;
  const next = withMagKnown(fitted.def, fitted.item, known);
  if (next === fitted.item) return weapon;
  return weaponWithMod(weapon, "mag", next);
}

/**
 * `def.stats` with every fitted attachment folded in. Always a fresh
 * `WeaponStats` (never the authored literal). `recoilMult`, `zoom` and
 * `suppressed` are not folded — the simulation has no recoil, camera or audio;
 * see `recoilMultiplier`, `opticZoom`, `hasSuppressor`.
 */
export function aggregateWeaponStats(
  def: WeaponItemDef,
  item: ItemInstance | null,
): WeaponStats {
  const s = def.stats;
  const out: WeaponStats = {
    rpm: s.rpm,
    magSize: s.magSize,
    reloadSec: s.reloadSec,
    muzzleVelocity: s.muzzleVelocity,
    damage: s.damage,
    penPower: s.penPower,
    falloffStartM: s.falloffStartM,
    falloffEndM: s.falloffEndM,
    falloffMinMult: s.falloffMinMult,
    ergonomics: s.ergonomics,
    adsTimeSec: s.adsTimeSec,
    hipSpreadDeg: s.hipSpreadDeg,
    zeroRangeM: s.zeroRangeM,
  };

  let ergoAdd = 0;
  for (const { def: att } of fittedModsDeep(def, item)) {
    const m = att.modifiers;
    if (m.hipSpreadMult !== undefined) out.hipSpreadDeg *= m.hipSpreadMult;
    if (m.muzzleVelocityMult !== undefined) out.muzzleVelocity *= m.muzzleVelocityMult;
    if (m.reloadSecMult !== undefined) out.reloadSec *= m.reloadSecMult;
    if (m.ergoAdd !== undefined) ergoAdd += m.ergoAdd;
    if (m.zeroRangeSetM !== undefined) out.zeroRangeM = m.zeroRangeSetM;
  }

  // The magazine dimension: whatever box is in it IS the capacity, an empty
  // well is zero. The guard is for a hypothetical gun with no `mag` slot.
  if (weaponFeedsFromMag(def)) {
    out.magSize = fittedMagazine(def, item)?.def.magCapacity ?? 0;
  }

  // Ergonomics clamped before anything reads it; ADS time recomputed from the
  // clamped aggregate, never multiplied. A bare gun reproduces its authored
  // `adsTimeSec` bit for bit (`+ 0` and a no-op clamp are float-exact).
  const ergo = out.ergonomics + ergoAdd;
  out.ergonomics = ergo < 0 ? 0 : ergo > 100 ? 100 : ergo;
  out.adsTimeSec = adsTimeForErgo(out.ergonomics);
  return out;
}

/**
 * Product of every fitted `recoilMult`, or 1. Client-side only — recoil is
 * drawn, not simulated.
 */
export function recoilMultiplier(def: WeaponItemDef, item: ItemInstance | null): number {
  let mult = 1;
  for (const { def: att } of fittedModsDeep(def, item)) {
    if (att.modifiers.recoilMult !== undefined) mult *= att.modifiers.recoilMult;
  }
  return mult;
}

/** True when any fitted part carries the `suppressed` flag. Drives the client's
 *  gunshot synthesis and nothing else — see `StatMods.suppressed`. */
export function hasSuppressor(def: WeaponItemDef, item: ItemInstance | null): boolean {
  for (const { def: att } of fittedModsDeep(def, item)) {
    if (att.modifiers.suppressed === true) return true;
  }
  return false;
}

/**
 * Which kind of device is bolted to this gun, or `null` for a bare rail — the
 * one place the roster becomes something the renderer can draw, used by both
 * server (whether T publishes `tac`) and client. A hardcoded two-entry map so
 * an unknown `tactical` part stays dark rather than guessing. Deep (a rifle's
 * rail is on its handguard); first in deep order wins. Says nothing about
 * stats — `laser`'s `hipSpreadMult` is unconditional.
 */
export function fittedTacticalKind(
  def: WeaponItemDef,
  item: ItemInstance | null,
): TacticalKind | null {
  for (const { def: att } of fittedModsDeep(def, item)) {
    const kind = TACTICAL_KINDS[att.id];
    if (kind !== undefined) return kind;
  }
  return null;
}

/** The whole of what this build knows how to switch on. See above for why it is
 *  a literal table and not a predicate over `attachKind`. */
const TACTICAL_KINDS: { readonly [defId: string]: TacticalKind } = {
  flashlight: "lamp",
  laser: "laser",
};

/**
 * The magnification aimed through, or `null` for irons and unmagnified glass.
 * Last `zoom` in deep order wins. An empty optic slot is a bare gun — the old
 * `defaultZoom` fallback is gone; the factory glass is a part with its own `zoom`.
 */
export function opticZoom(def: WeaponItemDef, item: ItemInstance | null): number | null {
  let zoom: number | null = null;
  for (const { def: att } of fittedModsDeep(def, item)) {
    if (att.attachKind !== "optic") continue;
    zoom = att.modifiers.zoom ?? null;
  }
  return zoom;
}

/**
 * Every weapon that can END UP wearing `partId`, in roster order — a tree walk,
 * since a red dot reaches the Eastern carbine through its railed top cover.
 * Bounded by `MAX_MOD_DEPTH` and a visited set (a content cycle must not hang
 * a tooltip; the set doubles as memoization). Defs, so the caller prints them.
 * Lives here rather than `db.ts` because this file already reads the registry.
 */
export function compatibleHosts(partId: string): readonly WeaponItemDef[] {
  const out: WeaponItemDef[] = [];
  for (const weapon of WEAPON_ITEMS) {
    // Fresh per weapon: the set guards against walking in circles inside ONE
    // gun's tree, and is not a cache shared across the roster.
    if (hostAccepts(weapon, partId, 1, new Set([weapon.id]))) out.push(weapon);
  }
  return out;
}

/**
 * Whether `host`'s slots lead to `partId`, directly or through a part of a
 * part. `depth` is the depth of the parts those slots hold (1 for a gun's own).
 */
function hostAccepts(
  host: WeaponItemDef | AttachmentItemDef,
  partId: string,
  depth: number,
  seen: Set<string>,
): boolean {
  if (depth > MAX_MOD_DEPTH) return false;
  for (const slot of host.slots ?? []) {
    for (const accepted of slot.accepts) {
      if (accepted === partId) return true;
      if (seen.has(accepted)) continue;
      seen.add(accepted);
      const def = getItemDef(accepted);
      // Only a part with slots of its own leads anywhere further; anything else
      // is a leaf, and an id that resolves to nothing is a leaf too.
      if (def === null || def.kind !== "attachment" || def.slots === undefined) continue;
      if (hostAccepts(def, partId, depth + 1, seen)) return true;
    }
  }
  return false;
}

/**
 * Everything DIRECTLY associated with `defId`, for browsing — one hop, unlike
 * `compatibleHosts`: a player right-clicking the Eastern carbine wants its own
 * furniture and cartridge, not glass that fits only after a different cover.
 * A gun → its slots' whitelists and its calibre's ammo. A part → the guns and
 * parts whose slots name it, whatever its own slots name, and (true magazine)
 * its calibre's ammo. A round → every gun and magazine of its calibre.
 * Everything else → nothing. Never itself. Emitted in `ALL_ITEM_DEFS` order.
 */
export function linkedDefIds(defId: string): readonly string[] {
  const def = getItemDef(defId);
  if (def === null) return [];
  const linked = new Set<string>();

  if (def.kind === "weapon") {
    for (const slot of def.slots ?? []) for (const id of slot.accepts) linked.add(id);
    for (const ammo of AMMO_ITEMS) if (ammo.caliber === def.caliber) linked.add(ammo.id);
  } else if (def.kind === "attachment") {
    // Upwards: guns AND parts whose slots name this one — a foregrip's hosts
    // are handguards.
    for (const host of [...WEAPON_ITEMS, ...ATTACHMENT_ITEMS]) {
      for (const slot of host.slots ?? []) {
        if (slot.accepts.includes(defId)) {
          linked.add(host.id);
          break;
        }
      }
    }
    // Downwards: what this part is itself a mounting point for.
    for (const slot of def.slots ?? []) for (const id of slot.accepts) linked.add(id);
    // And the one part that is about ammunition rather than about handling.
    if (isTrueMagazine(def)) {
      for (const ammo of AMMO_ITEMS) if (ammo.caliber === def.caliber) linked.add(ammo.id);
    }
  } else if (def.kind === "ammo") {
    for (const weapon of WEAPON_ITEMS) if (weapon.caliber === def.caliber) linked.add(weapon.id);
    for (const part of ATTACHMENT_ITEMS) {
      if (isTrueMagazine(part) && part.caliber === def.caliber) linked.add(part.id);
    }
  } else {
    return [];
  }

  linked.delete(defId);
  return ALL_ITEM_DEFS.filter((d) => linked.has(d.id)).map((d) => d.id);
}
